import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gameAPI } from '../api/game';
import PersianDatePicker from './common/PersianDatePicker';
import OwlDelivery from './OwlDelivery';
import styles from './SecretMessage.module.css';
import { sanitizePhoneInput, toEnglishDigits } from '../utils/phone';

const BASE_URL = window.location.origin;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatJalali(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

// یک کارت «نامه» جدا با ظاهر یک نامه‌ی واقعی — مهر + فرستنده + متن.
// برای اطلاعیه‌های داخلی (وزارت سحر و جادو، قطار، سن) و نامه‌های سفارشی
// ارسالی از پنل ادمین یکسان استفاده می‌شود تا همه شکل یک نامه جدا داشته باشند.
function LetterNotice({ sectionTitle, seal, fromLabel, toLabel, body, borderColor, children }) {
  return (
    <section className={styles.section}>
      {sectionTitle && <h2 className={styles.sectionTitle}>{sectionTitle}</h2>}
      <div className={styles.letterCard} style={borderColor ? { borderColor } : undefined}>
        <div className={styles.letterHeader}>
          <span className={styles.letterSeal}>{seal}</span>
          <div>
            {fromLabel && <div className={styles.letterFrom}>{fromLabel}</div>}
            {toLabel && <div className={styles.letterTo}>{toLabel}</div>}
          </div>
        </div>
        {body && <p className={styles.letterBody}>{body}</p>}
        {children}
      </div>
    </section>
  );
}

// ─── نشان رسمی هاگوارتز — سپرِ چهار گروه، SVG کدنویسی‌شده (بدون تصویر) ─────────
function HogwartsCrest({ size = 88 }) {
  return (
    <svg
      viewBox="0 0 120 140"
      width={size}
      height={(size * 140) / 120}
      style={{ display: 'block', margin: '0 auto 4px' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="hcGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e9c766" />
          <stop offset="100%" stopColor="#9a7d2e" />
        </linearGradient>
      </defs>
      {/* بدنه‌ی سپر */}
      <path
        d="M60 6 L112 20 V72 C112 103 91 124 60 134 C29 124 8 103 8 72 V20 Z"
        fill="#f3e9cf"
        stroke="url(#hcGold)"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      {/* چهار خانه — گریفیندور، ریونکلا، هافلپاف، اسلیترین */}
      <path d="M60 11 L107 23 V70 H60 Z" fill="#740001" />
      <path d="M60 11 L13 23 V70 H60 Z" fill="#222f5b" />
      <path d="M107 70 H60 V129 C82 121 100 105 107 84 Z" fill="#ecb939" />
      <path d="M13 70 H60 V129 C38 121 20 105 13 84 Z" fill="#1a472a" />
      {/* جداکننده‌ها */}
      <path d="M60 11 V129 M13 70 H107" stroke="url(#hcGold)" strokeWidth="2.5" fill="none" />
      {/* نماد گروه‌ها */}
      <text x="84" y="52" textAnchor="middle" fontSize="20">🦁</text>
      <text x="36" y="52" textAnchor="middle" fontSize="20">🦅</text>
      <text x="84" y="102" textAnchor="middle" fontSize="20">🦡</text>
      <text x="36" y="102" textAnchor="middle" fontSize="20">🐍</text>
      {/* قلب سپر — H */}
      <circle cx="60" cy="70" r="15" fill="#2a1a0a" stroke="url(#hcGold)" strokeWidth="2.5" />
      <text
        x="60"
        y="78"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontWeight="700"
        fontSize="20"
        fill="#e9c766"
      >
        H
      </text>
    </svg>
  );
}

// ─── خط تزئینی جوهری بین بخش‌های نامه ────────────────────────────────────────
function Flourish({ flip = false }) {
  return (
    <svg
      viewBox="0 0 220 22"
      width="180"
      height="18"
      style={{ display: 'block', margin: '8px auto', opacity: 0.75, transform: flip ? 'scaleY(-1)' : undefined }}
      aria-hidden="true"
    >
      <path
        d="M8 11 C48 -2 76 24 110 11 C144 -2 172 24 212 11"
        fill="none"
        stroke="#8a6f42"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="110" cy="11" r="2.6" fill="#8a6f42" />
      <path d="M2 11 h9 M209 11 h9" stroke="#8a6f42" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Stars() {
  return (
    <div className={styles.stars} aria-hidden="true">
      {Array.from({ length: 40 }).map((_, i) => (
        <span
          key={i}
          className={styles.star}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 4}s`,
            width: `${1 + Math.random() * 2}px`,
            height: `${1 + Math.random() * 2}px`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Steps ───────────────────────────────────────────────────────────────────

const STEP = {
  LOADING: 'loading',
  PHONE: 'phone',
  OTP: 'otp',
  BIRTHDATE: 'birthdate',
  SECRET: 'secret',
  ERROR: 'error',
};

export default function SecretMessage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';

  const [step, setStep] = useState(STEP.LOADING);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [existingBirthDate, setExistingBirthDate] = useState(null);
  const [confirmingBirthDate, setConfirmingBirthDate] = useState(false);
  const [data, setData] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [apologyLang, setApologyLang] = useState('fa');
  const [letterLangs, setLetterLangs] = useState({});
  // انیمیشن جغد نامه‌رسان — برای هر نفر (هر کد دعوت) فقط «یک بار» نمایش داده
  // می‌شود؛ در localStorage ذخیره می‌شود تا بین نشست‌ها هم ماندگار بماند.
  const [owlDone, setOwlDone] = useState(() => {
    try {
      return !!localStorage.getItem(`owl_seen_${code}`);
    } catch {
      return false; // حالت خصوصی مرورگر — انیمیشن نمایش داده می‌شود ولی ذخیره نمی‌شود
    }
  });

  // ── On mount: check invitation status ──────────────────────────────────────
  useEffect(() => {
    if (!code) {
      setError('لینک نامعتبر است. کد دعوت‌نامه یافت نشد.');
      setStep(STEP.ERROR);
      return;
    }

    // Try auto-view with stored credentials
    const stored = sessionStorage.getItem(`secret_${code}`);
    if (stored) {
      const { phone: savedPhone } = JSON.parse(stored);
      autoView(savedPhone);
    } else {
      checkStatus();
    }
  }, [code]);

  async function checkStatus() {
    try {
      const res = await gameAPI.secretCheck(code);
      const d = res.data;
      if (!d.success) throw new Error(d.message);
      setFirstName(d.recipientFirstName || '');
      if (d.claimed) {
        // Already claimed — need phone to view
        setStep(STEP.PHONE);
      } else {
        // Not claimed yet — start claim flow
        setStep(STEP.PHONE);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setStep(STEP.ERROR);
    }
  }

  async function autoView(savedPhone) {
    try {
      const res = await gameAPI.secretView(code, savedPhone);
      const d = res.data;
      if (d.success) {
        setPhone(savedPhone);
        setData(d);
        setStep(STEP.SECRET);
      } else {
        sessionStorage.removeItem(`secret_${code}`);
        checkStatus();
      }
    } catch {
      sessionStorage.removeItem(`secret_${code}`);
      checkStatus();
    }
  }

  // ── Request OTP ────────────────────────────────────────────────────────────
  async function handleRequestOTP(e) {
    e.preventDefault();
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setError('شماره موبایل معتبر وارد کنید');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await gameAPI.secretOTPRequest(code, phone);
      const d = res.data;
      if (!d.success) throw new Error(d.message);
      setOtpSent(true);
      setStep(STEP.OTP);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Verify OTP ─────────────────────────────────────────────────────────────
  async function handleVerifyOTP(e) {
    e.preventDefault();
    if (!otp || otp.length < 4) {
      setError('کد تأیید را وارد کنید');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await gameAPI.secretOTPVerify(code, phone, otp);
      const d = res.data;
      if (!d.success) throw new Error(d.message);
      sessionStorage.setItem(`secret_${code}`, JSON.stringify({ phone }));
      if (d.needsBirthDate) {
        // اگر تاریخ تولد قبلاً (مثلاً از ثبت‌نام در لیست انتظار با همین شماره) ثبت
        // شده، دوباره فرم خالی نشان نمی‌دهیم — همان مقدار را نشان می‌دهیم و فقط
        // تأیید می‌گیریم. کاربر می‌تواند در صورت اشتباه بودن آن را ویرایش کند.
        if (d.existingBirthDate) {
          setExistingBirthDate(d.existingBirthDate);
          setBirthDate(d.existingBirthDate);
          setConfirmingBirthDate(true);
        } else {
          setConfirmingBirthDate(false);
        }
        setStep(STEP.BIRTHDATE);
      } else {
        loadSecretView();
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Set birth date ─────────────────────────────────────────────────────────
  async function handleSetBirthDate(e) {
    e.preventDefault();
    if (!birthDate) {
      setError('تاریخ تولد را وارد کنید');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await gameAPI.secretSetBirthDate(code, phone, birthDate);
      if (!res.data.success) throw new Error(res.data.message);
      loadSecretView();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── View secret content ────────────────────────────────────────────────────
  async function loadSecretView() {
    try {
      const res = await gameAPI.secretView(code, phone);
      const d = res.data;
      if (!d.success) throw new Error(d.message);
      setData(d);
      setStep(STEP.SECRET);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setStep(STEP.ERROR);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (step === STEP.LOADING) {
    return (
      <div className={styles.root}>
        <Stars />
        <div className={styles.card}>
          <div className={styles.seal}>🦉</div>
          <p className={styles.loadingText}>در حال بارگذاری پیام محرمانه...</p>
        </div>
      </div>
    );
  }

  if (step === STEP.ERROR) {
    return (
      <div className={styles.root}>
        <Stars />
        <div className={styles.card}>
          <div className={styles.seal}>⚠️</div>
          <h2 className={styles.title}>خطا</h2>
          <p className={styles.errorMsg}>{error}</p>
          <a href="/" className={styles.linkBtn}>بازگشت به صفحه اصلی</a>
        </div>
      </div>
    );
  }

  if (step === STEP.PHONE) {
    return (
      <div className={styles.root}>
        <Stars />
        <div className={styles.card}>
          <div className={styles.seal}>🦉</div>
          <h2 className={styles.title}>پیام محرمانه هاگوارتز</h2>
          {firstName && <p className={styles.subtitle}>برای {firstName} عزیز</p>}
          <p className={styles.desc}>برای مشاهده پیام محرمانه‌ات، شماره موبایل خود را وارد کن.</p>
          {error && <p className={styles.errorMsg}>{error}</p>}
          <form onSubmit={handleRequestOTP} className={styles.form}>
            <input
              type="tel"
              placeholder="۰۹۱۲۳۴۵۶۷۸۹"
              value={phone}
              onChange={e => setPhone(sanitizePhoneInput(e.target.value))}
              className={styles.input}
              dir="ltr"
              inputMode="numeric"
            />
            <button type="submit" className={styles.btn} disabled={loading}>
              {loading ? 'در حال ارسال...' : 'ارسال کد تأیید'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEP.OTP) {
    return (
      <div className={styles.root}>
        <Stars />
        <div className={styles.card}>
          <div className={styles.seal}>📜</div>
          <h2 className={styles.title}>کد تأیید</h2>
          <p className={styles.desc}>کد ارسال‌شده به <strong dir="ltr">{phone}</strong> را وارد کن.</p>
          {error && <p className={styles.errorMsg}>{error}</p>}
          <form onSubmit={handleVerifyOTP} className={styles.form}>
            <input
              type="text"
              placeholder="کد ۶ رقمی"
              value={otp}
              onChange={e => setOtp(toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, 6))}
              className={styles.input}
              dir="ltr"
              inputMode="numeric"
              maxLength={6}
            />
            <button type="submit" className={styles.btn} disabled={loading}>
              {loading ? 'در حال تأیید...' : 'تأیید کد'}
            </button>
          </form>
          <button className={styles.linkBtn} onClick={() => { setStep(STEP.PHONE); setError(''); }}>
            اصلاح شماره
          </button>
        </div>
      </div>
    );
  }

  if (step === STEP.BIRTHDATE) {
    // اگر تاریخ تولد قبلاً ثبت شده (از لیست انتظار)، فقط تأیید می‌گیریم و
    // فرم خالی نشان نمی‌دهیم — کاربر می‌تواند در صورت نیاز آن را ویرایش کند.
    if (confirmingBirthDate && existingBirthDate) {
      return (
        <div className={styles.root}>
          <Stars />
          <div className={styles.card}>
            <div className={styles.seal}>🎂</div>
            <h2 className={styles.title}>تاریخ تولد</h2>
            <p className={styles.desc}>تاریخ تولدت قبلاً برای ما ثبت شده. آیا این تاریخ درست است؟</p>
            <p className={styles.title} style={{ fontSize: '1.1rem' }}>{formatJalali(existingBirthDate)}</p>
            {error && <p className={styles.errorMsg}>{error}</p>}
            <form onSubmit={handleSetBirthDate} className={styles.form}>
              <button type="submit" className={styles.btn} disabled={loading}>
                {loading ? 'در حال ذخیره...' : 'بله، درست است'}
              </button>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => setConfirmingBirthDate(false)}
                disabled={loading}
              >
                این تاریخ اشتباه است، ویرایش می‌کنم
              </button>
            </form>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.root}>
        <Stars />
        <div className={styles.card}>
          <div className={styles.seal}>🎂</div>
          <h2 className={styles.title}>تاریخ تولد</h2>
          <p className={styles.desc}>برای ثبت اطلاعاتت در هاگوارتز، تاریخ تولدت را وارد کن.</p>
          {error && <p className={styles.errorMsg}>{error}</p>}
          <form onSubmit={handleSetBirthDate} className={styles.form}>
            <PersianDatePicker
              className={styles.input}
              value={birthDate}
              outputFormat="gregorian-date-only"
              onChange={v => setBirthDate(v)}
              maxDate={new Date()}
            />
            <button type="submit" className={styles.btn} disabled={loading}>
              {loading ? 'در حال ذخیره...' : 'ادامه'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Secret Page ───────────────────────────────────────────────────────────

  const inv = data?.invitation;
  const giftGalleons = data?.giftGalleons ?? 40;
  const missedTrain = data?.missedTrain;
  const missedTrainMessage = data?.missedTrainMessage;
  const links = data?.links || {};
  const under11Notice = data?.under11Notice;
  const apologyLetter = data?.apologyLetter;
  const ministryPostNotice = data?.ministryPostNotice;
  const secretLetters = data?.secretLetters || [];
  const requirementCategories = data?.requirementCategories || [];

  return (
    <div className={styles.root}>
      {/* ── انیمیشن سینمایی جغد نامه‌رسان (یک بار در هر نشست) ── */}
      {!owlDone && (
        <OwlDelivery
          firstName={inv?.recipientFirstName}
          lastName={inv?.recipientLastName}
          addressLines={[inv?.addressLine1, inv?.addressLine2, inv?.addressLine3]}
          onDone={() => {
            try {
              localStorage.setItem(`owl_seen_${code}`, '1');
            } catch { /* حالت خصوصی — مهم نیست */ }
            setOwlDone(true);
          }}
        />
      )}
      <Stars />
      <div className={styles.secretWrapper}>

        {/* ── Header ── */}
        <div className={styles.secretHeader}>
          <div className={styles.hogwartsLogo}>⚡</div>
          <h1 className={styles.secretTitle}>پیام محرمانه هاگوارتز</h1>
          <p className={styles.secretSubtitle}>مدرسه علوم و فنون جادوگری هاگوارتز</p>
        </div>

        {/* ── اطلاعیه‌ی وزارت سحر و جادو (توقف موقت پست جغدی مشتنگ‌ها) ── */}
        {ministryPostNotice && (
          <LetterNotice
            sectionTitle="🏛️ اطلاعیه وزارت سحر و جادو"
            seal="🏛️"
            fromLabel="وزارت سحر و جادو، اداره‌ی پست جادوگری"
            body={ministryPostNotice}
            borderColor="#d4af37"
          />
        )}

        {/*
          ── بررسی تاریخ تولد قبل از نمایش محتوا ──────────────────────────────
          اگر کاربر هنوز ۱۱ سالش نشده، فقط یادداشت جغد («صبر کن») نشان داده
          می‌شود و به محتوای شخصی (نامه‌های سفارشی، نامه عذرخواهی، دعوت‌نامه،
          بلیت، وسایل، گالیون، لینک‌های ثبت‌نام) دسترسی پیدا نمی‌کند — چون این‌ها
          هنوز به او تعلق نمی‌گیرد. کاربران ۱۱ سال به بالا محتوای کامل را می‌بینند.
        */}
        {under11Notice ? (
          <LetterNotice
            sectionTitle="🦉 یادداشت جغد ما"
            seal="🦉"
            fromLabel="وزارت سحر و جادو"
            body={under11Notice}
            borderColor="#d4af37"
          />
        ) : (
          <>
            {/* ── قطارت را از دست دادی؟ ── */}
            {missedTrain && (
              <LetterNotice
                sectionTitle="🚂 هاگوارتز اکسپرس"
                seal="🚂"
                fromLabel="ایستگاه کینگزکراس، سکوی ۹ و ۳/۴"
                body={missedTrainMessage}
                borderColor="#d4af37"
              />
            )}

            {/* ── نامه‌های محرمانه‌ی سفارشی (ساخته‌شده از پنل ادمین) ── */}
            {secretLetters.map((letter) => {
              const lang = letterLangs[letter.id] || 'fa';
              const hasBoth = letter.bodyFa && letter.bodyEn;
              return (
                <LetterNotice
                  key={letter.id}
                  sectionTitle={letter.title || '📩 نامه محرمانه'}
                  seal={letter.seal || '🦉'}
                  fromLabel={letter.fromLabel}
                  body={lang === 'en' ? (letter.bodyEn || letter.bodyFa) : (letter.bodyFa || letter.bodyEn)}
                  borderColor={letter.borderColor}
                >
                  {hasBoth && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button
                        onClick={() => setLetterLangs(prev => ({ ...prev, [letter.id]: 'fa' }))}
                        className={styles.linkBtn}
                        style={{ fontWeight: lang === 'fa' ? 700 : 400, marginTop: 0 }}
                      >
                        فارسی
                      </button>
                      <button
                        onClick={() => setLetterLangs(prev => ({ ...prev, [letter.id]: 'en' }))}
                        className={styles.linkBtn}
                        style={{ fontWeight: lang === 'en' ? 700 : 400, marginTop: 0 }}
                      >
                        English
                      </button>
                    </div>
                  )}
                </LetterNotice>
              );
            })}

            {/* ── نامه عذرخواهی (رایگان، فقط دیجیتالی) ── */}
            {apologyLetter && (apologyLetter.fa || apologyLetter.en) && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>🦉 نامه عذرخواهی هاگوارتز</h2>
                <div className={styles.letterCard}>
                  <div className={styles.letterHeader}>
                    <span className={styles.letterSeal}>🦉</span>
                    <div>
                      <div className={styles.letterFrom}>مدرسه علوم و فنون جادوگری هاگوارتز</div>
                      <div className={styles.letterTo}>به {inv?.recipientFirstName || 'دوست'} عزیز،</div>
                    </div>
                  </div>
                  {apologyLetter.fa && apologyLetter.en && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      <button
                        onClick={() => setApologyLang('fa')}
                        className={styles.linkBtn}
                        style={{ fontWeight: apologyLang === 'fa' ? 700 : 400 }}
                      >
                        فارسی
                      </button>
                      <button
                        onClick={() => setApologyLang('en')}
                        className={styles.linkBtn}
                        style={{ fontWeight: apologyLang === 'en' ? 700 : 400 }}
                      >
                        English
                      </button>
                    </div>
                  )}
                  <p className={styles.letterBody} dir={apologyLang === 'en' ? 'ltr' : 'rtl'}>
                    {apologyLang === 'en' ? (apologyLetter.en || apologyLetter.fa) : (apologyLetter.fa || apologyLetter.en)}
                  </p>
                  <div style={{ color: '#888', fontSize: 12, marginTop: 8 }}>این هدیه‌ای رایگان و صرفاً دیجیتالی است 🎁</div>
                </div>
              </section>
            )}

            {/* ── دعوت‌نامه — دقیقاً به شکل نامه‌ی رسمی پذیرش هاگوارتز (کاغذ کهنه + مهر موم) ── */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>📜 دعوت‌نامه</h2>
              <div className={styles.parchmentCard}>
                <div className={styles.waxSeal}>H</div>
                <div className={styles.inkFrame}>
                  <HogwartsCrest />
                  <div className={styles.crestMotto}>Draco Dormiens Nunquam Titillandus</div>
                  <h3 className={styles.parchmentSchool}>مدرسه‌ی علوم و فنون جادوگری هاگوارتز</h3>
                  <Flourish />
                  <p className={styles.parchmentHeadmaster}>
                    مدیر: آلبوس دامبلدور<br />
                    <small>(نشان مرلین، درجه‌ی یک، رئیس انجمن بین‌المللی جادوگران، جادوگر ارشد ویزنگاموت)</small>
                  </p>
                  <div className={styles.parchmentBody}>
                    <p className={styles.parchmentSalutation}>{inv?.recipientFirstName} {inv?.recipientLastName} عزیز،</p>
                    <p>
                      با کمال مسرت به اطلاع می‌رسانیم که جای شما در مدرسه‌ی علوم و فنون جادوگری هاگوارتز
                      محفوظ است. فهرست کتاب‌ها و وسایل مورد نیاز سال اول در همین نامه برایتان آمده است.
                    </p>
                    <p>
                      قطار هاگوارتز اکسپرس در تاریخ <strong>{inv?.departureDate ? formatJalali(inv.departureDate) : '—'}</strong> رأس
                      ساعت <strong>{inv?.departureTime || '11:00'}</strong> از سکوی <strong>{inv?.platform || '9¾'}</strong> حرکت
                      خواهد کرد. لطفاً پیش از حرکت قطار در ایستگاه حاضر شوید.
                    </p>
                  </div>
                  <Flourish flip />
                  <p className={styles.parchmentSign}>
                    ارادتمند شما،
                    <span className={styles.signature}>مینروا مک‌گونگال</span>
                    <svg viewBox="0 0 160 14" width="130" height="12" style={{ display: 'block', margin: '0 auto', opacity: 0.6 }} aria-hidden="true">
                      <path d="M6 8 C40 2 60 13 84 7 C108 1 130 12 154 5" fill="none" stroke="#3b2a10" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                    <small>معاون مدرسه</small>
                  </p>
                  <div className={styles.parchmentCode}>
                    کد ثبت‌نام: <code>{inv?.code}</code>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#8a6f42' }}>(هنگام ثبت‌نام وارد کنید)</div>
                  </div>
                  {inv?.addressLine1 && (
                    <div className={styles.parchmentAddress}>
                      <div>{inv.addressLine1}</div>
                      {inv.addressLine2 && <div>{inv.addressLine2}</div>}
                      {inv.addressLine3 && <div>{inv.addressLine3}</div>}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* ── بلیت قطار ── */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>🚂 بلیت هاگوارتز اکسپرس</h2>
              <div className={styles.parchmentTicket}>
                <div className={styles.ticketOrnateFrame} aria-hidden="true" />
                <div className={styles.ticketInner}>
                  {/* ── ته‌بلیت (سمت راست) — سکوی ۹ و ۳/۴ ── */}
                  <div className={styles.ticketStub}>
                    <div className={styles.ticketStubLabel}>سکوی</div>
                    <div className={styles.ticketStubPlatform}>{inv?.platform || '9¾'}</div>
                    <div className={styles.ticketStubNote}>ایستگاه کینگزکراس</div>
                    <div className={styles.ticketStubSerial} dir="ltr">№ {inv?.ticketCode || '———'}</div>
                  </div>

                  {/* ── بدنه‌ی اصلی بلیت ── */}
                  <div className={styles.ticketMain}>
                    <div className={styles.ticketBrandRow}>
                      <span className={styles.ticketRule} aria-hidden="true" />
                      <span className={styles.ticketBrand}>هاگوارتز اکسپرس</span>
                      <span className={styles.ticketRule} aria-hidden="true" />
                    </div>
                    <div className={styles.ticketSubBrand}>راه‌آهن جادویی بریتانیا ✦ بلیت یک‌طرفه</div>
                    <div className={styles.parchmentTicketRoute}>
                      لندن <span className={styles.routeArrow}>➤</span> هاگوارتز
                    </div>
                    <div className={styles.ticketPassenger}>
                      <small>مسافر</small>
                      <strong>{inv?.recipientFirstName} {inv?.recipientLastName}</strong>
                    </div>
                    <div className={styles.parchmentTicketGrid}>
                      <div><small>تاریخ حرکت</small><strong>{inv?.departureDate ? formatJalali(inv.departureDate) : '—'}</strong></div>
                      <div><small>ساعت حرکت</small><strong>{inv?.departureTime || '11:00'}</strong></div>
                    </div>
                    <div className={styles.parchmentTicketFoot}>
                      <span>کد بلیت</span>
                      <code dir="ltr">{inv?.ticketCode}</code>
                    </div>

                    {/* ── لوکوموتیو بخار — سیلوئت SVG با دودِ متحرک ── */}
                    <svg viewBox="0 0 240 74" className={styles.trainArt} aria-hidden="true">
                      <g fill="rgba(240, 199, 94, 0.20)">
                        {/* واگن و کابین */}
                        <rect x="6" y="34" width="66" height="24" rx="3" />
                        <rect x="76" y="24" width="30" height="34" rx="3" />
                        {/* دیگ بخار */}
                        <rect x="106" y="32" width="76" height="24" rx="11" />
                        {/* دودکش و گنبد */}
                        <rect x="160" y="16" width="12" height="18" rx="2" />
                        <rect x="132" y="24" width="12" height="10" rx="4" />
                        {/* جلوپنجره */}
                        <path d="M182 56 L200 56 L188 40 Z" />
                        {/* چرخ‌ها */}
                        <circle cx="24" cy="60" r="8" />
                        <circle cx="52" cy="60" r="8" />
                        <circle cx="92" cy="60" r="9" />
                        <circle cx="128" cy="60" r="11" />
                        <circle cx="162" cy="60" r="8" />
                      </g>
                      {/* دود */}
                      <g fill="rgba(240, 199, 94, 0.28)">
                        <circle className={styles.steam1} cx="170" cy="12" r="5" />
                        <circle className={styles.steam2} cx="182" cy="6" r="7" />
                        <circle className={styles.steam3} cx="198" cy="2" r="9" />
                      </g>
                    </svg>
                  </div>
                </div>
                {inv?.isUsed && <div className={styles.usedBadge}>✅ استفاده شده</div>}
              </div>
            </section>

            {/* ── لیست وسایل سال اول — مستقیم و کامل، روی کاغذ کهنه، درست مثل نامه‌ی سنتی هاگوارتز ── */}
            {requirementCategories.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>🛒 لیست وسایل سال اول</h2>
                <div className={styles.suppliesParchment}>
                  <HogwartsCrest size={72} />
                  <h3 className={styles.parchmentSchool} style={{ textAlign: 'center' }}>مدرسه‌ی علوم و فنون جادوگری هاگوارتز</h3>
                  <Flourish />
                  <p className={styles.parchmentHeadmaster} style={{ textAlign: 'center' }}>
                    به مدیریت آلبوس دامبلدور<br />
                    <small>(نشان مرلین درجه‌ی یک، رئیس انجمن بین‌المللی جادوگران، جادوگر ارشد ویزنگاموت)</small>
                  </p>
                  <p className={styles.suppliesIntro}>اقلام مورد نیاز دانش‌آموزان سال اول:</p>

                  {requirementCategories.map((cat) => (
                    <div key={cat.category} className={styles.suppliesCat}>
                      <h4 className={styles.suppliesCatTitle}>{cat.label}</h4>
                      <ol className={styles.suppliesList}>
                        {cat.items.map((item) => (
                          <li key={item._id} className={styles.suppliesItem}>
                            <span className={styles.suppliesRow}>
                              <span className={styles.suppliesItemName}>
                                {item.farsiName || item.name}
                                {item.isMandatory && <span className={styles.suppliesItemMandatory}>اجباری</span>}
                              </span>
                              {(item.priceGalleons > 0 || item.priceSickles > 0 || item.priceKnuts > 0) && (
                                <>
                                  <span className={styles.dotLeader} aria-hidden="true" />
                                  <span className={styles.suppliesItemPrice}>
                                    {[
                                      item.priceGalleons > 0 ? `${item.priceGalleons} گالیون` : null,
                                      item.priceSickles > 0 ? `${item.priceSickles} سیکل` : null,
                                      item.priceKnuts > 0 ? `${item.priceKnuts} نات` : null
                                    ].filter(Boolean).join(' و ')}
                                  </span>
                                </>
                              )}
                            </span>
                            {(item.farsiDescription || item.description) && (
                              <span className={styles.suppliesItemDesc}>{item.farsiDescription || item.description}</span>
                            )}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}

                  <p className={styles.suppliesNote}>
                    توجه داشته باشید که تمام کتاب‌های درسی و لباس‌ها باید با نام و نام خانوادگی دانش‌آموز نشان‌گذاری شوند.
                  </p>
                  <p className={styles.suppliesNote}>
                    هر دانش‌آموز می‌تواند صاحب یک جغد، یک گربه یا یک وزغ باشد.
                  </p>
                  <p className={styles.suppliesNote}>
                    لازم به ذکر است که دانش‌آموزان سال اول اجازه‌ی داشتن جاروی پرنده همراه را ندارند.
                  </p>
                  <p className={styles.suppliesFootSign}>
                    تمامی وسایل بالا را می‌توانید با گالیون‌های خود از بانک گرینگوتس در کوچه دیاگون تهیه کنید.
                  </p>
                  <Flourish flip />
                  <p className={styles.parchmentSign} style={{ marginTop: 8 }}>
                    <span className={styles.signature}>مینروا مک‌گونگال</span>
                    <small>معاون مدرسه</small>
                  </p>
                </div>
                <a
                  href={`/school-supplies?code=${inv?.code || ''}`}
                  className={styles.linkBtn}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  مشاهده تصاویر و جزئیات کامل در کوچه دیاگون ←
                </a>
              </section>
            )}

            {/* ── گالیون هدیه و بانک گرینگوتس ── */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>💰 گالیون هدیه</h2>
              <div className={styles.galleonCard}>
                <div className={styles.galleonAmount}>{giftGalleons}</div>
                <div className={styles.galleonLabel}>گالیون</div>
                <p className={styles.galleonDesc}>
                  با دریافت این دعوت‌نامه، پس از ثبت‌نام {giftGalleons} گالیون طلا به خزانه‌ی جادویی‌ات در بانک گرینگوتس واریز می‌شود.
                </p>
              </div>

              <div className={styles.bankCard} style={{ marginTop: 14 }}>
                <p className={styles.bankIntro}>
                  🏦 پول رایج دنیای جادوگری سه نوع سکه است: <strong>گالیون</strong> طلایی، <strong>سیکل</strong> نقره‌ای و{' '}
                  <strong>نات</strong> برنزی. این گالیون‌ها فقط عدد روی صفحه نیستند — مستقیماً در خزانه‌ی شخصی تو در{' '}
                  <strong>بانک جادوگران گرینگوتس</strong> نگهداری می‌شوند، همان بانکی که گابلین‌ها اداره‌اش می‌کنند.
                  برای خرید وسایل لیست بالا باید سری به گرینگوتس در کوچه دیاگون بزنی و از همان‌جا، با گالیون‌های خودت، خرید کنی.
                </p>
                <div className={styles.bankRates}>
                  <div className={styles.bankRateItem}>
                    <div className={styles.bankRateValue}>۱ گالیون</div>
                    <div className={styles.bankRateLabel}>= ۱۷ سیکل</div>
                  </div>
                  <div className={styles.bankRateItem}>
                    <div className={styles.bankRateValue}>۱ سیکل</div>
                    <div className={styles.bankRateLabel}>= ۲۹ نات</div>
                  </div>
                  <div className={styles.bankRateItem}>
                    <div className={styles.bankRateValue}>۱ گالیون</div>
                    <div className={styles.bankRateLabel}>= ۴۹۳ نات</div>
                  </div>
                </div>
                <p className={styles.bankNote}>
                  خزانه‌ات بلافاصله پس از ثبت‌نام باز می‌شود — کافی‌ست وارد کوچه دیاگون شوی تا گابلین‌های گرینگوتس راهنمایی‌ات کنند.
                </p>
                <a
                  href={links.diagonAlley || '/dashboard'}
                  className={styles.linkBtn}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', textAlign: 'center' }}
                >
                  ورود به بانک گرینگوتس در کوچه دیاگون ←
                </a>
              </div>
            </section>

            {/* ── لینک‌ها ── */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>🔗 لینک‌های مهم</h2>
              <div className={styles.linksGrid}>
                <a
                  href={`/invitation/register?code=${inv?.code || ''}`}
                  className={styles.linkCard}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={styles.linkIcon}>🏛️</span>
                  <span className={styles.linkName}>ورود به سایت</span>
                  <span className={styles.linkDesc}>ثبت‌نام با کد دعوت</span>
                </a>

                <a
                  href="/kings-cross"
                  className={styles.linkCard}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={styles.linkIcon}>🚪</span>
                  <span className={styles.linkName}>ایستگاه کینگزکراس</span>
                  <span className={styles.linkDesc}>سکوی ۹ و ۳/۴ — ورود به هاگوارتز</span>
                </a>

                <a
                  href={links.diagonAlley || '/dashboard'}
                  className={styles.linkCard}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={styles.linkIcon}>🛍️</span>
                  <span className={styles.linkName}>کوچه دیاگون</span>
                  <span className={styles.linkDesc}>خرید وسایل جادوگری</span>
                </a>
              </div>
            </section>
          </>
        )}

        {/* ── Footer ── */}
        <div className={styles.secretFooter}>
          <p>این پیام محرمانه است و فقط برای {inv?.recipientFirstName} صادر شده است.</p>
          <p className={styles.footerNote}>🦉 ارسال‌شده توسط جغدهای هاگوارتز</p>
        </div>

      </div>
    </div>
  );
}
